import React, { useState, useEffect } from "react";
import { Icon } from "../UIElements/Icon";
import { Button } from "../UIElements/Button";
import OrderCardSimple from "../Orders/OrderCardSimple";
import OrderDetailsModal from "../Orders/OrderDetailsModal";
// import ConversationModal from '../Modals/ConversationModal';
import Pagination from "../Data/Pagination";
import OrdersLoadingSimple from "../Orders/OrdersLoadingSimple";
import OrdersError from "../Orders/OrdersError";
import {
  useUserOrders,
  useUserOrdersStats,
} from "../../../hooks/useUserOrders";
import {
  ITEMS_PER_PAGE,
  MAX_VISIBLE_PAGES,
} from "@finitless/shared/orderConstants";

const OrderHistoryTab = ({ userId }) => {
  const [activeFilter, setActiveFilter] = useState("active");
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  // const [showConversationModal, setShowConversationModal] = useState(false);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Get order counts and data for all tabs (cached)
  const {
    active,
    completed,
    cancelled,
    isLoading: statsLoading,
    activeData,
    completedData,
    cancelledData,
  } = useUserOrdersStats({
    userId,
    searchTerm,
  });

  // Get current tab orders (only if not cached or different page)
  const {
    data: ordersData,
    isLoading: ordersLoading,
    error: ordersError,
  } = useUserOrders({
    userId,
    statusFilter: activeFilter,
    searchTerm,
    currentPage,
    itemsPerPage: ITEMS_PER_PAGE,
  });

  // Use cached data if available and it's the first page, otherwise use fresh data
  const getCachedData = () => {
    if (currentPage === 1) {
      switch (activeFilter) {
        case "active":
          return activeData;
        case "completed":
          return completedData;
        case "cancelled":
          return cancelledData;
        default:
          return ordersData;
      }
    }
    return ordersData;
  };

  const cachedData = getCachedData();
  const orders = cachedData?.orders || ordersData?.orders || [];
  const paginationData = cachedData?.pagination || ordersData?.pagination;
  const totalPages =
    paginationData?.totalPages ||
    Math.ceil((paginationData?.total || 0) / ITEMS_PER_PAGE);

  const filterTabs = [
    { key: "active", label: "Active", color: "text-warning" },
    { key: "completed", label: "Complete", color: "text-success" },
    { key: "cancelled", label: "Cancelled", color: "text-error" },
  ];

  const getFilterCount = (filterKey) => {
    // Use cached stats data for all tabs
    const countMap = {
      active: active || 0,
      completed: completed || 0,
      cancelled: cancelled || 0,
    };
    return countMap[filterKey] || 0;
  };

  const handleFilterChange = (newFilter) => {
    setActiveFilter(newFilter);
    setCurrentPage(1);
  };

  const handleSearchChange = (term) => {
    setSearchInput(term);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setShowDetailsModal(true);
  };

  const handleViewConversation = (order) => {
    setSelectedOrder(order);
    // setShowConversationModal(true);
  };

  const handleCloseModals = () => {
    setShowDetailsModal(false);
    // setShowConversationModal(false);
    setSelectedOrder(null);
  };

  const isLoading = ordersLoading || statsLoading;
  const error = ordersError;

  return (
    <div className="space-y-6">
      {/* Filter Tabs */}
      <div className="border-border border-b">
        <nav className="flex gap-4 overflow-x-auto sm:gap-8">
          {filterTabs.map(({ key, label, color }) => {
            const count = getFilterCount(key);
            return (
              <Button
                variant="ghost"
                size="touch"
                key={key}
                onClick={() => handleFilterChange(key)}
                className={`border-b-2 px-1 py-3 font-medium transition-colors ${
                  activeFilter === key
                    ? `border-current ${color}`
                    : "text-muted-foreground hover:border-border hover:text-foreground border-transparent"
                }`}
              >
                {label} ({count})
              </Button>
            );
          })}
        </nav>
      </div>

      {/* Search - Only show if there are orders */}
      {(orders.length > 0 || searchTerm) && (
        <div className="flex justify-start">
          <div className="relative w-full max-w-md">
            <div className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-3">
              <Icon name="Search" className="text-muted-foreground h-5 w-5" />
            </div>
            <input
              disabled={isLoading || error}
              type="text"
              placeholder="Search by order ID..."
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="border-border bg-card text-foreground placeholder:text-muted-foreground focus:ring-ring min-h-11 w-full rounded-lg border px-4 py-2 ps-10 pe-12 text-[length:var(--font-size-product-ui)] focus:border-transparent focus:ring-2 focus:outline-none"
            />
            {searchInput && (
              <Button
                variant="ghost"
                disabled={isLoading}
                onClick={handleClearSearch}
                size="iconTouch"
                className="bg-muted hover:bg-accent absolute inset-y-0 end-0 my-auto flex cursor-pointer items-center justify-center rounded-full transition-colors"
                type="button"
              >
                <Icon
                  name="X"
                  className="text-foreground hover:text-foreground h-3 w-3"
                />
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Content */}
      {isLoading ? (
        <OrdersLoadingSimple />
      ) : error ? (
        <OrdersError error={error} />
      ) : orders.length > 0 ? (
        <>
          <div className="space-y-3">
            {orders.map((order) => (
              <OrderCardSimple
                key={order.id}
                order={order}
                onViewDetails={handleViewDetails}
                onViewConversation={handleViewConversation}
              />
            ))}
          </div>

          {/* Pagination */}
          <div className="mt-8">
            <div className="border-border bg-background rounded-lg border p-4">
              <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
                {/* Pagination Info */}
                <div className="text-muted-foreground text-sm">
                  Showing{" "}
                  <span className="text-foreground font-medium">
                    {(currentPage - 1) * ITEMS_PER_PAGE + 1}
                  </span>{" "}
                  to{" "}
                  <span className="text-foreground font-medium">
                    {Math.min(
                      currentPage * ITEMS_PER_PAGE,
                      paginationData?.total || 0,
                    )}
                  </span>{" "}
                  of{" "}
                  <span className="text-foreground font-medium">
                    {paginationData?.total || 0}
                  </span>{" "}
                  results
                </div>

                {/* Pagination Controls */}
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  maxVisiblePages={MAX_VISIBLE_PAGES}
                  disabled={isLoading}
                  className="mt-4 sm:mt-0"
                />
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="py-12 text-center">
          <Icon
            name="ShoppingBag"
            className="text-muted-foreground mx-auto mb-4 h-12 w-12"
          />
          <h3 className="text-foreground mt-2 text-sm font-medium">
            No orders found
          </h3>
          <p className="text-muted-foreground mt-1 text-sm">
            {searchTerm
              ? "Try adjusting your search terms."
              : "No orders found for this user."}
          </p>
        </div>
      )}

      {/* Modals */}
      <OrderDetailsModal
        order={selectedOrder}
        isOpen={showDetailsModal}
        onClose={handleCloseModals}
      />

      {/* <ConversationModal
        order={selectedOrder}
        isOpen={showConversationModal}
        onClose={handleCloseModals}
      /> */}
    </div>
  );
};

export default OrderHistoryTab;
